/**
 * Attaches an AAD bearer token via X-Custom-Auth (not Authorization — Azure Static Web Apps'
 * Free tier overwrites that header with its own platform token) and calls the API.
 */
import { PublicClientApplication } from '@azure/msal-browser'
import { apiTokenScopes } from '../authConfig'

let msalInstance: PublicClientApplication | null = null

export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance
}

/** localStorage key holding a temporary dev-impersonation token (see docs/DEV_AUTH.md). When set,
 * `apiFetch` sends it as `X-Dev-Auth` instead of a real MSAL token and `main.tsx` skips the
 * interactive sign-in — letting the deployed app be exercised from a plain browser as a fixed
 * admin. Only works if the API has `DEV_AUTH_ENABLED=true`. */
export const DEV_AUTH_TOKEN_KEY = 'stackradar.devAuthToken'

export function getDevAuthToken(): string | null {
  try {
    return localStorage.getItem(DEV_AUTH_TOKEN_KEY)
  } catch {
    return null
  }
}

async function getAccessToken(): Promise<string> {
  if (!msalInstance) throw new Error('MSAL not initialized')
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) throw new Error('Not signed in')

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiTokenScopes, account })
    return result.accessToken
  } catch {
    // Silent renewal can fail for more reasons than InteractionRequiredAuthError alone — e.g. the
    // hidden iframe timing out (BrowserAuthError: monitor_window_timeout) after a long-idle
    // session, which used to fall through uncaught and surface as a raw error on the page instead
    // of recovering. Redirect (not acquireTokenPopup) since this call is usually reached from a
    // background data fetch, not a direct click — a popup opened outside a user gesture gets
    // blocked by the browser's popup blocker far more often than not, while a redirect always
    // works and matches how the initial sign-in already happens (main.tsx's loginRedirect()).
    await msalInstance.acquireTokenRedirect({ scopes: apiTokenScopes, account })
    // The line above navigates the page away — this point is never actually reached.
    throw new Error('Redirecting to sign-in...')
  }
}

export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  // Dev impersonation wins over everything else (works in both dev and prod builds) — set via
  // localStorage, e.g. `localStorage.setItem('stackradar.devAuthToken', '<token>')`. See
  // docs/DEV_AUTH.md; only works when the API has DEV_AUTH_ENABLED=true.
  const devToken = getDevAuthToken()
  if (devToken) {
    return fetch(input, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        'X-Dev-Auth': devToken,
      },
    })
  }

  const token = await getAccessToken()
  return fetch(input, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      'X-Custom-Auth': `Bearer ${token}`,
    },
  })
}

/** apiFetch + JSON parse, throwing with the API's own error message on a non-OK response */
export async function apiFetchJson<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(input, init)
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    throw new Error((body as { error?: string } | null)?.error ?? `Request failed: ${res.status}`)
  }
  return body as T
}
